/**
 * AGR-NEXT-1 — continuous membership: tune-up OBLIGATIONS. When a Pegasus
 * agreement is SOLD, we write down every tune-up the membership owes over its
 * whole term (a furnace check each fall, an A/C check each spring, doubled for a
 * second system) as `agreement_visit` rows (source='generated'). Nobody has to
 * remember them — they surface on the "Tune-ups Due" worklist as each season
 * nears, and one click materializes a real pending_schedule dispatch.
 *
 * Reuses the existing agreement_visit model (target_date/status/fulfilled_dispatch)
 * rather than a parallel table — extend what owns the concept (declutter).
 * Competitor frame: ServiceTitan Memberships auto-generate "recurring services"
 * onto a to-schedule worklist; adopted, with a human still booking each one.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { loadPlanTicketLines, type TicketLine } from "@/features/agreements/planCoverage";

export interface TuneupObligation {
  id: string;
  custno: string;
  locno: string | null;
  plan_sale_id: string | null;
  task_label: string;       // "Furnace tune-up"
  covers_category: string;  // "heating" | "cooling"
  sequence_label: string;   // "Furnace tune-up · 1 of 3"
  target_date: string;      // YYYY-MM-DD
  status: string;           // open | scheduled | completed | skipped
  fulfilled_dispatch: string | null;
  term_start: string | null;
  term_end: string | null;
  // DERIVED (not stored): the latest-dated check of this plan's term = the
  // in-person renewal moment. Computed in useTuneupsDue, never hand-flagged.
  is_renewal_check: boolean;
}

// The seasonal template. Sedgwick's PLUS plan covers heating + cooling: a
// furnace check each fall (October) and an A/C check each spring (May). This is
// the DEFAULT — each base plan can override it in Settings → Plan Pricing
// (pegasus_plan_price.tuneups), so a furnace-only or plumbing plan generates the
// right visits. NULL/empty on a plan = use this default.
export interface TuneupTemplateItem { category: string; label: string; month: number }
export const DEFAULT_TUNEUP_TEMPLATE: TuneupTemplateItem[] = [
  { category: "heating", label: "Furnace Tune-Up", month: 10 },
  { category: "cooling", label: "A/C Tune-Up", month: 5 },
];

/** Validate a stored (jsonb) template → usable items; fall back to the default
 *  if it's missing/empty/garbage (a bad config must never block a sale). */
export function normalizeTemplate(raw: unknown): TuneupTemplateItem[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_TUNEUP_TEMPLATE;
  const out: TuneupTemplateItem[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const label = String(o.label ?? "").trim();
    const month = Number(o.month);
    if (!label || !(month >= 1 && month <= 12)) continue;
    out.push({ category: String(o.category ?? "heating").trim() || "heating", label, month });
  }
  return out.length ? out : DEFAULT_TUNEUP_TEMPLATE;
}

// MEMBERSHIPS-03 rule 5 — seasons are WINDOWS, not dates. A new plan's first
// check of each kind goes to the CURRENT season if it is still open with at
// least `cutoffDays` left, otherwise the next one; later checks follow one
// season a year. A check done at the sale is check one (the caller marks the
// earliest row completed). Windows a NEW plan can claim: heating = Jul 1–Dec 31
// (fall), cooling = Feb 1–Aug 31 (spring/summer; late A/C checks still get
// DELIVERED into the fall, but a plan sold in September promises next spring's).
const SEASON_WINDOW: Record<string, { from: [number, number]; to: [number, number] }> = {
  heating: { from: [7, 1], to: [12, 31] },
  cooling: { from: [2, 1], to: [8, 31] },
};
const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const daysBetween = (a: string, b: string) => Math.round((new Date(b + "T00:00:00").getTime() - new Date(a + "T00:00:00").getTime()) / 86400000);

/** The season year a plan's first check of `category` belongs to. */
export function firstSeasonYear(startISO: string, category: string, cutoffDays = 30): number {
  const w = SEASON_WINDOW[category] ?? SEASON_WINDOW.heating;
  const y = Number(startISO.slice(0, 4));
  const from = iso(y, w.from[0], w.from[1]);
  const to = iso(y, w.to[0], w.to[1]);
  if (startISO < from) return y;                                  // before this year's window opens
  if (startISO <= to && daysBetween(startISO, to) >= cutoffDays) return y; // inside it, with room
  return y + 1;                                                    // too late this year
}

/** Target date for the k-th check: the template month in that season year,
 *  never before the plan starts (a November sale's furnace check is due now). */
export function checkTargetDate(startISO: string, category: string, month: number, k: number, cutoffDays = 30): string {
  const year = firstSeasonYear(startISO, category, cutoffDays) + k;
  const t = iso(year, month, 1);
  return k === 0 && t < startISO ? startISO : t;
}

export interface SaleForObligations {
  id: string;
  custno: string;
  locno: string | null;
  term: number;
  startDate: string;
  expireDate: string | null;
  secondSystem: boolean;
  base_id?: string | null; // the base plan — its tune-up template overrides the default
}

/** Pure builder — the obligation rows a sale should generate (testable). */
export function buildTuneupObligations(s: SaleForObligations, template: TuneupTemplateItem[] = DEFAULT_TUNEUP_TEMPLATE, cutoffDays = 30): Record<string, unknown>[] {
  if (!s.startDate || !s.term) return [];
  const now = new Date().toISOString();
  const systems = s.secondSystem ? 2 : 1;
  const agrmt = `PEG-${s.id.slice(0, 8)}`; // agrmt_no is NOT NULL — synthesize from the sale id
  const rows: Record<string, unknown>[] = [];
  for (const t of template) {
    for (let sys = 1; sys <= systems; sys++) {
      const sysTag = systems > 1 ? ` (system ${sys})` : "";
      for (let k = 0; k < s.term; k++) {
        rows.push({
          custno: s.custno,
          locno: s.locno,
          agrmt_no: agrmt,
          plan_sale_id: s.id,
          term_start: s.startDate,
          term_end: s.expireDate,
          task: "tuneup",
          task_label: `${t.label}${sysTag}`,
          covers_category: t.category,
          sequence_label: `${t.label}${sysTag} · ${k + 1} of ${s.term}`,
          target_date: checkTargetDate(s.startDate, t.category, t.month, k, cutoffDays),
          status: "open",
          source: "generated",
          created_at: now,
          updated_at: now,
        });
      }
    }
  }
  // ONE SEQUENCE (34i93, Seth: "a 3-year plan is 6 checks — 1 of 6 … 6 of 6; 1 year
  // is 2, 2 years 4"): the checks are numbered across BOTH kinds in the order
  // they come due, the way ESC's "X of Y" reads on the phone.
  rows.sort((a, b) => String(a.target_date).localeCompare(String(b.target_date)) || String(a.covers_category).localeCompare(String(b.covers_category)));
  rows.forEach((r, i) => {
    r.seq_no = i + 1;
    r.seq_total = rows.length;
    r.sequence_label = `${r.task_label} · ${i + 1} of ${rows.length}`;
  });
  return rows;
}

/** Write the obligations for a freshly-sold agreement. Looks up the base plan's
 *  configured tune-up template (Settings → Plan Pricing), falling back to the
 *  default. Best-effort: a failure here must not block the sale (it already
 *  saved) — caller logs + continues. */
export async function generateTuneupObligations(s: SaleForObligations): Promise<void> {
  let template = DEFAULT_TUNEUP_TEMPLATE;
  if (s.base_id) {
    const { data } = await supabase.from("pegasus_plan_price").select("tuneups").eq("id", s.base_id).maybeSingle();
    template = normalizeTemplate((data as { tuneups?: unknown } | null)?.tuneups);
  }
  // Settings → Plan Rules: how many days must be left in a season for a new
  // plan to claim it (MEMBERSHIPS-03, default 30).
  let cutoffDays = 30;
  try {
    const { data: rule } = await supabase.from("pegasus_plan_config").select("value").eq("key", "season_cutoff_days").maybeSingle();
    const n = Number((rule as { value?: unknown } | null)?.value);
    if (Number.isFinite(n) && n >= 0) cutoffDays = n;
  } catch { /* the default stands */ }
  const rows = buildTuneupObligations(s, template, cutoffDays);
  if (!rows.length) return;
  const { error } = await supabase.from("agreement_visit").insert(rows);
  if (error) throw error;
}

export interface TuneupDueRow extends TuneupObligation { customer_name: string }

/** The "Tune-ups Due" worklist — open + already-scheduled generated obligations,
 *  soonest first, with the customer name resolved. */
export function useTuneupsDue() {
  return useQuery({
    queryKey: ["tuneups_due"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TuneupDueRow[]> => {
      const { data, error } = await supabase
        .from("agreement_visit")
        .select("id,custno,locno,plan_sale_id,task_label,covers_category,sequence_label,target_date,status,fulfilled_dispatch,term_start,term_end")
        .eq("source", "generated")
        .in("status", ["open", "scheduled"])
        .order("target_date", { ascending: true })
        .limit(1000);
      if (error) throw error;
      const rows = (data ?? []) as Omit<TuneupObligation, "is_renewal_check">[];
      const custnos = [...new Set(rows.map((r) => r.custno))];
      const names = new Map<string, string>();
      if (custnos.length) {
        const { data: cs } = await supabase.from("mirror_customer").select("custno,fullname,firstname,lastname").in("custno", custnos);
        for (const c of (cs ?? []) as { custno: string; fullname: string | null; firstname: string | null; lastname: string | null }[]) {
          names.set(c.custno, (c.fullname?.trim() || `${c.firstname ?? ""} ${c.lastname ?? ""}`.trim() || c.custno));
        }
      }
      // Renewal moment = the LAST check of each plan's term (max target_date over
      // ALL its generated obligations, incl. ones already completed/scheduled).
      // Derived, never stored (feedback_derive_dont_flag) — regenerating or
      // skipping a check can't leave a stale "renewal" flag behind.
      const planIds = [...new Set(rows.map((r) => r.plan_sale_id).filter(Boolean))] as string[];
      const lastByPlan = new Map<string, string>();
      if (planIds.length) {
        const { data: mx } = await supabase
          .from("agreement_visit")
          .select("plan_sale_id,target_date")
          .eq("source", "generated")
          .in("plan_sale_id", planIds)
          .order("target_date", { ascending: false });
        for (const m of (mx ?? []) as { plan_sale_id: string; target_date: string }[]) {
          if (!lastByPlan.has(m.plan_sale_id)) lastByPlan.set(m.plan_sale_id, m.target_date); // desc order → first seen is the max
        }
      }
      return rows.map((r) => ({
        ...r,
        customer_name: names.get(r.custno) ?? r.custno,
        is_renewal_check: !!r.plan_sale_id && lastByPlan.get(r.plan_sale_id) === r.target_date,
      }));
    },
  });
}

/** One-click: materialize a tune-up obligation into a real pending_schedule
 *  dispatch (drops into the schedule queue) + link it back to the obligation. */
export function useScheduleTuneup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (o: TuneupDueRow) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      let lat: number | null = null, lng: number | null = null;
      const { data: loc } = await supabase
        .from("mirror_location").select("latitude, longitude")
        .eq("custno", o.custno).eq("locno", o.locno ?? "00001").limit(1).maybeSingle();
      const g = loc as { latitude: number | null; longitude: number | null } | null;
      lat = g?.latitude ?? null; lng = g?.longitude ?? null;

      // PLAN-COVERAGE-TALKS (34i91): the ticket carries what the plan includes
      // and what the office approved — "Bring: APR35, APR501 · Charge: Air
      // Scrubber bulbs $267" — composed from the same model the Membership card
      // shows, and the parts land as structured rows for Parts for the Day.
      const plan = await loadPlanTicketLines(o.custno, o.locno).catch(() => ({ note: null, parts: [] as TicketLine[] }));

      const { data: disp, error } = await supabase.from("pegasus_dispatch").insert({
        status: "pending_schedule",
        lane_key: "incoming",
        custno: o.custno,
        locno: o.locno,
        lat, lng,
        customer_name: o.customer_name,
        summary: `${o.task_label} — membership tune-up${o.is_renewal_check ? " + renewal due" : ""}`,
        notes: `Auto-created from membership (${o.sequence_label}). Due around ${o.target_date}.`
          + (o.is_renewal_check ? " LAST CHECK OF THE TERM — offer the plan renewal at this visit." : "")
          + (plan.note ? ` ${plan.note}.` : ""),
        urgency: "routine",
        target_date: o.target_date,
        tags: o.is_renewal_check ? ["tuneup", "membership", "renewal"] : ["tuneup", "membership"],
        source: "manual",
        created_by: u?.user?.email ?? null,
        created_at: now,
        updated_at: now,
      }).select("id").single();
      if (error) throw error;
      const dispId = (disp as { id: string }).id;

      if (plan.parts.length) {
        // Best-effort: the note already carries the line; the rows feed Parts for the Day.
        await supabase.from("pegasus_dispatch_part").insert(plan.parts.map((p) => ({
          dispatch_id: dispId, label: p.label, qty: p.qty ?? 1, kind: p.kind ?? "part", source: "plan", created_by: u?.user?.email ?? null,
        })));
      }

      const { error: e2 } = await supabase.from("agreement_visit")
        .update({ status: "scheduled", fulfilled_dispatch: dispId, updated_at: now, updated_by: u?.user?.email ?? null })
        .eq("id", o.id);
      if (e2) throw e2;
      return dispId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tuneups_due"] });
      qc.invalidateQueries({ queryKey: ["pegasus_dispatch"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["today"] });
    },
  });
}

/** Mark a tune-up not needed (customer declined / system removed). */
export function useSkipTuneup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (o: TuneupObligation) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("agreement_visit")
        .update({ status: "skipped", updated_at: new Date().toISOString(), updated_by: u?.user?.email ?? null })
        .eq("id", o.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tuneups_due"] }),
  });
}
